import React from "react";
import { QuestionsIF } from "./QuestionsIF";
import { DocQuestion } from "./DocQuestion";
import { SelectionType } from "../uiControllers/AnswerButton";
import styled from "@mui/material/styles/styled";
import { useSymptomIcon } from "../../hooks/useSymptomIcon";
import { ClosedSingleQuestionView } from "./closedSingleQuestionView";
import { PatientColorMap } from "../../layout/patientColorMap";
import { QuestionDescription } from "./questionDescription";
import clsx from "clsx";
import { ShowInChatReasoningButton } from "../demodebug/mobileSuggestionZoom/showInChatReasoningButton";
import { ConversationQuestionType } from "fe-common/graphql";

//region [[ Styles ]]
const ClosedSingleQuestionContainerView = styled("div")({
  width: "100%",
});

const QuestionContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  width: "100%",
});

const SymptomIconContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  padding: "10px",
  marginBottom: "10px",
});

export const SymptomIconObject = styled("div")({
  "& svg": {
    height: "170px",
    width: "170px",
  },
  // shirt
  "& path[fill='#5A52C3']": {
    fill: PatientColorMap.Primary,
  },
  // dark body lines
  "& path[fill='#4841A8']": {
    fill: PatientColorMap.SymptomDarkBodyLines,
  },
  // circle bg
  "& path[fill='#D0CFE3']": {
    fill: PatientColorMap.SymptomIconBackground,
    filter: "drop-shadow( 0px 3px 10px rgba(0, 0, 0, 0.09))",
  },
  // light body lines
  "& path[fill='#D8B8B8']": {
    fill: PatientColorMap.SymptomLightBodyLines,
  },
  // skin
  "& path[fill='#FFE5E5'], path[fill='#FFE6E6']": {
    fill: PatientColorMap.SymptomCharacterColor,
  },
  "& path[fill='#ECD0D0']": {
    fill: PatientColorMap.SymptomCharacterColor,
    filter: "brightness(90%)",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]
interface ClosedSingleQuestionProps extends QuestionsIF {
  className?: string;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export function ClosedSingleQuestion(props: ClosedSingleQuestionProps) {
  const isAnswered = !!props.answer;

  const suggestion = props.question.suggestions[0];

  const symptomIcon = useSymptomIcon(suggestion?.suggestion?.id);

  const refinement =
    suggestion.refinements.length > 0 && suggestion.refinements[0];

  const useFeatureDescription = !refinement;

  const relation = suggestion.suggestion.qualifiers[0]?.relation;

  const onClick = (selection: SelectionType) => {
    const qualifiers = refinement
      ? [
          {
            relation,
            name: refinement.qualifiers[0].value.name,
            value: refinement.qualifiers[0].value,
            negate: false,
            range: refinement.qualifiers[0].range,
            freeText: refinement.qualifiers[0].freeText,
            source: null,
          },
        ]
      : suggestion.suggestion.qualifiers.filter((q) => q.value || q.range) ||
        []; // If it's not a refinement question, make sure to send back qualifiers with value or range, or empty array.

    switch (selection) {
      case SelectionType.YES: {
        props.onAnswer([
          {
            ...suggestion.suggestion,
            qualifiers,
          },
        ]);
        return;
      }
      case SelectionType.NO: {
        props.onAnswer([
          {
            ...suggestion.suggestion,
            qualifiers,
            negate: true,
          },
        ]);
        return;
      }
      case SelectionType.DONT_KNOW: {
        props.onAnswer([
          {
            ...suggestion.suggestion,
            qualifiers,
            skip: true,
          },
        ]);
        return;
      }
    }
  };

  const isBackgroundQuestion =
    props.question.questionType ===
    ConversationQuestionType.BackgroundSuggestion;

  return (
    <ClosedSingleQuestionContainerView
      className={clsx(props.className, "closed-single-question")}
    >
      <QuestionContainer className={"question-container"}>
        <DocQuestion
          text={suggestion.question.text}
          miniView={props.miniView}
        />
        {useFeatureDescription && (
          <QuestionDescription
            questionText={suggestion.question.text}
            qualifiedFeature={suggestion.suggestion.feature}
            featureId={suggestion?.suggestion?.feature?.id}
            isAnswered={isAnswered}
          />
        )}
        {!isAnswered && props.question.basedOnReasoning && (
          <ShowInChatReasoningButton
            suggestion={suggestion.suggestion}
            isBackgroundSuggestion={isBackgroundQuestion}
          />
        )}
      </QuestionContainer>
      {!isAnswered && (
        <ClosedSingleQuestionView
          gender={props.gender}
          canSkip={!!refinement || props.canSkip}
          onClick={onClick}
        >
          {symptomIcon && (
            <SymptomIconContainer>
              <SymptomIconObject>{symptomIcon}</SymptomIconObject>
            </SymptomIconContainer>
          )}
        </ClosedSingleQuestionView>
      )}
    </ClosedSingleQuestionContainerView>
  );
}
